import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Phone, Mail, User, Pencil, MailCheck, MailX, Download, Upload, Wallet, ChevronRight, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { StatementDialog } from "@/components/invoices/ChaseDialogs";
import { useCompany } from "@/context/CompanyContext";
import { hasModule } from "@/lib/modules";
import { DepotPicker, DepotTag } from "@/components/DepotPicker";
import { useDepot } from "@/context/DepotContext";
import { useClientExport, ExportClientsDialog, ImportClientsDialog } from "@/components/coord/ClientCsvDialogs";

const EMPTY = { name: "", contact: "", phone: "", email: "", address: "", email_report: true, depot_id: "" };

// Balance for a client: its own depot, or summed across depots for a company-wide client.
const balanceOf = (owing, c) => {
  const name = (c.name || "").trim().toLowerCase();
  if (c.depot_id) return owing[`${c.depot_id}|${name}`];
  return Object.values(owing).filter((o) => o.key.split("|").slice(1).join("|") === name)
    .reduce((t, o) => ({ owing_c: t.owing_c + o.owing_c, overdue_c: t.overdue_c + o.overdue_c }), { owing_c: 0, overdue_c: 0 });
};

// Unpaid balance on a client card; tap the balance for that client's invoices, or email a statement.
const ClientBalance = ({ o, id, onClick, onStatement }) => (
  !o?.owing_c ? <p className="flex items-center gap-2 pt-1 text-muted-foreground/70" data-testid={`client-balance-${id}`}><Wallet className="w-3.5 h-3.5" /> Nothing owing</p> : (
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      <button type="button" onClick={onClick} data-testid={`client-balance-${id}`}
        className="flex-1 min-w-[180px] flex items-center gap-x-2 rounded-lg bg-secondary/70 hover:bg-secondary px-2.5 py-1.5 text-left transition-colors">
        <Wallet className="w-3.5 h-3.5 text-foreground shrink-0" />
        <span className="flex flex-wrap gap-x-1.5">
          <span className="font-semibold text-foreground whitespace-nowrap">{fmtMoney(o.owing_c)} owing</span>
          {o.overdue_c > 0 && <span className="text-red-600 font-medium whitespace-nowrap" data-testid={`client-overdue-${id}`}>· {fmtMoney(o.overdue_c)} overdue</span>}
        </span>
        <ChevronRight className="w-3.5 h-3.5 ml-auto shrink-0" />
      </button>
      <button type="button" onClick={onStatement} title="Email a statement" data-testid={`client-statement-${id}`}
        className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1.5 text-xs font-medium text-foreground hover:bg-secondary transition-colors">
        <FileText className="w-3.5 h-3.5" /> Statement
      </button>
    </div>
  )
);

export const ClientsTab = ({ onShowInvoices }) => {
  const [clients, setClients] = useState([]);
  const [meta, setMeta] = useState({}); // client_id -> email_report
  const [owing, setOwing] = useState({}); // "depot|name" -> { owing_c, overdue_c, count }
  const [statementFor, setStatementFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // client being edited (null = create)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const exp = useClientExport();
  const { needsPick, isAdmin } = useDepot();
  const { company } = useCompany();
  const jobsOn = hasModule(company, "jobs");

  const load = async (quiet = false) => {
    if (quiet !== true) setLoading(true);
    try {
      const [cs, ms, ow] = await Promise.all([api.clients(), api.clientMeta().catch(() => []), onShowInvoices ? api.owingByClient().catch(() => ({ clients: [] })) : { clients: [] }]);
      setClients(cs);
      setMeta(Object.fromEntries((ms || []).map((m) => [m.client_id, m.email_report])));
      setOwing(Object.fromEntries(ow.clients.map((o) => [o.key, o])));
    } catch { toast.error("Failed to load clients"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name || "", contact: c.contact || "", phone: c.phone || "", email: c.email || "", address: c.address || "", email_report: meta[c.id] !== false });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error("Client name is required"); return; }
    if (!editing && needsPick && !form.depot_id) { toast.error("Choose a depot for this client"); return; }
    setSaving(true);
    const { email_report, depot_id, ...clientFields } = form;
    try {
      const saved = editing ? await api.updateClient(editing.id, clientFields) : await api.createClient({ ...clientFields, depot_id: depot_id || undefined });
      const cid = (saved && saved.id) || (editing && editing.id);
      if (cid) await api.setClientMeta(cid, email_report);
      toast.success(editing ? "Client updated" : "Client saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save client"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.deleteClient(id); toast.success("Client deleted"); load(); }
    catch { toast.error("Could not delete client"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold">Clients</h2>
          <p className="text-sm text-muted-foreground">{clients.length} client{clients.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" data-testid="export-clients-btn" onClick={exp.start} disabled={!clients.length && !isAdmin} className="rounded-full"><Download className="w-4 h-4 mr-2" /> Export CSV</Button>
          <Button variant="outline" data-testid="import-clients-btn" onClick={() => setImportOpen(true)} className="rounded-full"><Upload className="w-4 h-4 mr-2" /> Import clients</Button>
          <Button data-testid="add-client-btn" onClick={openCreate} className="rounded-full bg-primary hover:bg-primary/90 text-white"><Plus className="w-4 h-4 mr-2" /> Add client</Button>
        </div>
      </div>

      <ExportClientsDialog key={exp.open ? "open" : "closed"} open={exp.open} setOpen={exp.setOpen} run={exp.run} initial={exp.initial} />
      <ImportClientsDialog open={importOpen} setOpen={setImportOpen} onDone={() => load(true)} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit client" : "Add client"}</DialogTitle>
            <DialogDescription>Client contact details and report email preference.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!editing && <DepotPicker value={form.depot_id} onChange={(v) => setForm({ ...form, depot_id: v })} testId="client-depot-select" />}
            <div className="space-y-2"><Label>Company / name *</Label><Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Contact person</Label><Input data-testid="client-contact-input" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Phone</Label><Input data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-2"><Label>Email</Label><Input data-testid="client-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Site address</Label>
              <AddressAutocomplete testId="client-address-input" value={form.address} onChange={(v) => setForm({ ...form, address: v })} placeholder="Start typing an address…" /></div>
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium">Email completion reports</p>
                <p className="text-xs text-muted-foreground">Send this client the job report when a job is completed.</p>
              </div>
              <Switch data-testid="client-email-report-toggle" checked={form.email_report} onCheckedChange={(v) => setForm({ ...form, email_report: v })} />
            </div>
          </div>
          <DialogFooter><Button data-testid="client-create-submit" onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-white">{saving ? "Saving…" : (editing ? "Save changes" : "Save client")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {clients.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-2xl"><p className="text-muted-foreground">No clients yet.</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <div key={c.id} data-testid={`client-card-${c.id}`} className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0 space-y-1"><h3 className="font-semibold">{c.name}</h3><DepotTag id={c.depot_id} testId={`client-depot-${c.id}`} /></div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="rounded-full h-8 w-8"><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`remove-client-${c.id}`} onClick={() => remove(c.id)} className="text-destructive hover:bg-destructive/10 rounded-full h-8 w-8"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                {c.contact && <p className="flex items-center gap-2"><User className="w-3.5 h-3.5" /> {c.contact}</p>}
                {c.phone && <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {c.phone}</p>}
                {c.email && <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {c.email}</p>}
                {jobsOn && <p className="flex items-center gap-2 pt-1" data-testid={`client-email-report-${c.id}`}>
                  {meta[c.id] !== false
                    ? <><MailCheck className="w-3.5 h-3.5 text-emerald-500" /> <span className="text-emerald-600">Reports emailed</span></>
                    : <><MailX className="w-3.5 h-3.5 text-amber-500" /> <span className="text-amber-600">Reports off</span></>}
                </p>}
                {onShowInvoices && <ClientBalance o={balanceOf(owing, c)} id={c.id} onClick={() => onShowInvoices({ name: c.name, depot_id: c.depot_id })}
                  onStatement={() => setStatementFor({ name: c.name, depot_id: c.depot_id })} />}
              </div>
            </div>
          ))}
        </div>
      )}
      <StatementDialog client={statementFor} onClose={() => setStatementFor(null)} />
    </div>
  );
};
